/**
 * Utility functions for client data processing and formatting
 */

/**
 * Maps API response to component format
 */
export const mapClientData = (client) => {
  // Format document type: "ghana_card" -> "Ghana Card"
  const formatDocumentType = (docType) => {
    if (!docType) return 'N/A';
    return docType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Determine status based on verification
  const getStatus = () => {
    if (client.email_verified_at || client.phone_number_verified_at) {
      return 'Active';
    }
    return 'Pending';
  };

  return {
    id: client.slug || client.id || Math.random(),
    slug: client.slug,
    firstName: client.first_name || '',
    lastName: client.last_name || '',
    email: client.email || '',
    phone: client.phone_number || '',
    region: client.region || '',
    location: client.location || '',
    gender: client.gender || '',
    dob: client.dob || '',
    agentSlug: client.agent_slug || '',
    idType: formatDocumentType(client.document_type),
    cardNumber: client.card_number || '',
    issueDate: client.issue_date || '',
    expiryDate: client.expiry_date || '',
    idCardFront: client.id_card_front || '',
    idCardBack: client.id_card_back || '',
    emailVerified: !!client.email_verified_at,
    phoneVerified: !!client.phone_number_verified_at,
    status: getStatus(),
    registrationDate: client.created_at || client.createdAt || client.registration_date || client.registrationDate || client.date_created || client.dateCreated || '',
    lastActivity: client.updated_at || client.updatedAt || client.last_activity || client.lastActivity || '',
  };
};

/**
 * Formats date string to readable format
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

/**
 * Formats phone number for display
 */
export const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  // Format phone number for display
  if (phone.startsWith('233')) {
    return `+${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
  } else if (phone.startsWith('0')) {
    return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
  }
  return phone;
};

/**
 * Formats date of birth (DDMMYYYY) to readable format
 */
export const formatDOB = (dob) => {
  if (!dob) return 'N/A';
  // Format DDMMYYYY to readable date
  if (dob.length === 8) {
    const day = dob.slice(0, 2);
    const month = dob.slice(2, 4);
    const year = dob.slice(4, 8);
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  }
  return dob;
};

/**
 * Returns status color classes based on status
 */
export const getStatusColor = (status) => {
  const statusLower = (status || '').toLowerCase();
  switch (statusLower) {
    case 'active':
    case 'verified':
    case 'approved':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'pending':
    case 'processing':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'inactive':
    case 'suspended':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'rejected':
    case 'declined':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-blue-100 text-blue-800 border-blue-200';
  }
};

/**
 * Filters clients based on search term and status filter
 */
export const filterClients = (clients, searchTerm, selectedFilter) => {
  return clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      (client.firstName || '').toLowerCase().includes(searchLower) ||
      (client.lastName || '').toLowerCase().includes(searchLower) ||
      (client.email || '').toLowerCase().includes(searchLower) ||
      (client.phone || '').includes(searchTerm) ||
      (client.region || '').toLowerCase().includes(searchLower) ||
      (client.location || '').toLowerCase().includes(searchLower) ||
      (client.cardNumber || '').toLowerCase().includes(searchLower);
    
    const matchesFilter = selectedFilter === 'all' || 
      (client.status || '').toLowerCase() === selectedFilter.toLowerCase();
    
    return matchesSearch && matchesFilter;
  });
};

/**
 * Calculates statistics from clients array
 */
export const calculateStats = (clients) => {
  return {
    total: clients.length,
    active: clients.filter(c => (c.status || '').toLowerCase() === 'active').length,
    pending: clients.filter(c => (c.status || '').toLowerCase() === 'pending').length,
    regions: new Set(clients.map(c => c.region).filter(Boolean)).size,
  };
};
